import React from "react";
import { Alert, FloatingLabel, Form, InputGroup } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { CustomProjectModalProps } from "../CustomProjectModal.types.tsx";

import { ActionButton } from "../../../components/Buttons.tsx";
import { GreenLabel } from "../../../components/Labels.tsx";
import { Divider } from "../../../components/Inputs.tsx";

interface Props extends CustomProjectModalProps {
  onBack: () => void;
}

const CustomProjectStepTwo: React.FC<Props> = ({
  formData,
  handleChange,
  handleSelectGeneric,
  handleSubmit,
  onBack,
  unitSystem,
}) => {
  const { t } = useTranslation();

  const disableNumberScroll = (e: React.WheelEvent<HTMLInputElement>) => {
    e.currentTarget.blur();
  };

  return (
    <Form noValidate onSubmit={handleSubmit}>
      {formData?.technology_id === "4" && (
        <>
          <Alert variant="info" className="mb-3">
            {t(`customProjectModal.step2.centrifugalInfo`)}
          </Alert>

          {/* Model Size */}
          <FloatingLabel
            controlId="floating_model_size"
            label={t(`customProjectModal.step2.modelSize`)}
            className="mb-3"
          >
            <Form.Select
              name="custom_system[model_size]"
              value={formData.custom_system.model_size}
              onChange={handleSelectGeneric}
            >
              <option value="" disabled>
                {t("customProjectModal.step1.selectOption")}
              </option>
              <option value="80">80</option>
              <option value="100">100</option>
            </Form.Select>
          </FloatingLabel>

          {/* Range Selection */}
          <FloatingLabel
            controlId="floating_range_selection"
            label={t(`customProjectModal.step2.rangeSelection`)}
            className="mb-3"
          >
            <Form.Select
              name="custom_system[range_selection]"
              value={formData.custom_system.range_selection}
              onChange={handleSelectGeneric}
            >
              <option value="" disabled>
                {t("customProjectModal.step1.selectOption")}
              </option>
              <option value="Baseline">Baseline</option>
              <option value="Extended">Extended</option>
            </Form.Select>
          </FloatingLabel>

          {/* Running Capacity */}
          <InputGroup className="mb-3">
            <FloatingLabel
              controlId="floating_running_capacity"
              label={t(`customProjectModal.step2.runningCapacity`)}
            >
              <Form.Control
                type="number"
                name="custom_system[running_capacity]"
                value={formData.custom_system.running_capacity}
                onChange={handleChange}
                required
                placeholder=""
                onWheel={disableNumberScroll}
              />
            </FloatingLabel>
            <InputGroup.Text>{t(`units.${unitSystem}.weight`)}</InputGroup.Text>
          </InputGroup>

          {/* Is Economizer */}
          <Form.Check
            className="mb-3"
            type="switch"
            name="custom_system[is_economizer]"
            label={t(`customProjectModal.step2.isEconomizer`)}
            checked={Boolean(formData.custom_system.is_economizer)}
            onChange={handleChange}
          />

          {formData.custom_system.is_economizer && (
            <>
              {/* Approach Temperature */}
              <InputGroup className="mb-3">
                <FloatingLabel
                  controlId="floating_approach_temperature"
                  label={t(`customProjectModal.step2.approachTemperature`)}
                >
                  <Form.Control
                    type="number"
                    name="custom_system[approach_temperature]"
                    value={formData.custom_system.approach_temperature}
                    onChange={handleChange}
                    required
                    step="any"
                    placeholder=""
                    onWheel={disableNumberScroll}
                  />
                </FloatingLabel>
                <InputGroup.Text>{t(`units.${unitSystem}.temperature`)}</InputGroup.Text>
              </InputGroup>

              {/* Economizer Superheat */}
              <InputGroup className="mb-3">
                <FloatingLabel
                  controlId="floating_economizer_superheat"
                  label={t(`customProjectModal.step2.economizerSuperheat`)}
                >
                  <Form.Control
                    type="number"
                    name="custom_system[economizer_superheat]"
                    value={formData.custom_system.economizer_superheat}
                    onChange={handleChange}
                    required
                    step="any"
                    placeholder=""
                    onWheel={disableNumberScroll}
                  />
                </FloatingLabel>
                <InputGroup.Text>{t(`units.${unitSystem}.temperature`)}</InputGroup.Text>
              </InputGroup>
            </>
          )}

          <Divider />
        </>
      )}

      {/* Compressor Model */}
      <FloatingLabel
        controlId="floating_compressor_model"
        label={t(`customProjectModal.step2.compressorModel`)}
        className="mb-3"
      >
        <Form.Control
          type="text"
          name="custom_system[compressor_model]"
          value={formData.custom_system.compressor_model}
          onChange={handleChange}
          required
          placeholder=""
        />
      </FloatingLabel>

      {/* Cooling Capacity per Compressor */}
      <InputGroup className="mb-3">
        <FloatingLabel
          controlId="floating_cooling_capacity_per_compressor"
          label={t(`customProjectModal.step2.coolingCapacityPerCompressor`)}
        >
          <Form.Control
            type="number"
            name="custom_system[cooling_capacity_per_compressor]"
            value={formData.custom_system.cooling_capacity_per_compressor}
            onChange={handleChange}
            required
            step="any"
            placeholder=""
            onWheel={disableNumberScroll}
            min={0}
          />
        </FloatingLabel>
        <InputGroup.Text>BTU/h</InputGroup.Text>
      </InputGroup>

      {/* kW per Compressor*/}
      <InputGroup className="mb-3">
        <FloatingLabel
          controlId="floating_kw_per_compressor"
          label={t(`customProjectModal.step2.kwPerCompressor`)}
        >
          <Form.Control
            type="number"
            name="custom_system[kw_per_compressor]"
            value={formData.custom_system.kw_per_compressor}
            onChange={handleChange}
            required
            placeholder=""
            onWheel={disableNumberScroll}
            min={0}
            step="any"
          />
        </FloatingLabel>
        <InputGroup.Text>kW</InputGroup.Text>
      </InputGroup>

      {/* Amps per Compressor */}
      <InputGroup className="mb-3">
        <FloatingLabel
          controlId="floating_amps_per_compressor"
          label={t(`customProjectModal.step2.ampsPerCompressor`)}
        >
          <Form.Control
            type="number"
            name="custom_system[amps_per_compressor]"
            value={formData.custom_system.amps_per_compressor}
            onChange={handleChange}
            required
            step="any"
            placeholder=""
            onWheel={disableNumberScroll}
            min={0}
          />
        </FloatingLabel>
        <InputGroup.Text>A</InputGroup.Text>
      </InputGroup>

      {/* Heat Capacity per Compressor */}
      <InputGroup className="mb-3">
        <FloatingLabel
          controlId="floating_heat_capacity_per_compressor"
          label={t(`customProjectModal.step2.heatCapacityPerCompressor`)}
        >
          <Form.Control
            type="number"
            name="custom_system[heat_capacity_per_compressor]"
            value={formData.custom_system.heat_capacity_per_compressor}
            onChange={handleChange}
            required
            placeholder=""
            onWheel={disableNumberScroll}
            min={0}
            step="any"
          />
        </FloatingLabel>
        <InputGroup.Text>BTU/h</InputGroup.Text>
      </InputGroup>

      {/* Compressor RLA */}
      <FloatingLabel
        controlId="floating_compressor_rla"
        label={t(`customProjectModal.step2.compressorRLA`)}
        className="mb-3"
      >
        <Form.Control
          type="number"
          name="custom_system[compressor_rla]"
          value={formData.custom_system.compressor_rla}
          onChange={handleChange}
          required
          placeholder=""
          onWheel={disableNumberScroll}
          min={0}
          step="any"
        />
      </FloatingLabel>

      {/* Compressor LRA */}
      <FloatingLabel
        controlId="floating_compressor_lra"
        label={t(`customProjectModal.step2.compressorLRA`)}
        className="mb-3"
      >
        <Form.Control
          type="number"
          name="custom_system[compressor_lra]"
          value={formData.custom_system.compressor_lra}
          onChange={handleChange}
          required
          placeholder=""
          onWheel={disableNumberScroll}
          min={0}
          step="any"
        />
      </FloatingLabel>

      {/* Circuit Count */}
      <FloatingLabel
        controlId="floating_circuit_count"
        label={t(`customProjectModal.step2.circuitCount`)}
        className="mb-3"
      >
        <Form.Control
          type="number"
          name="custom_system[circuit_count]"
          value={formData.custom_system.circuit_count}
          onChange={handleChange}
          required
          placeholder=""
          onWheel={disableNumberScroll}
          min={1}
          max={4}
          step="any"
        />
      </FloatingLabel>

      {/* Compressor Quantity per Circuit */}
      <FloatingLabel
        controlId="floating_compressor_quantity_per_circuit"
        label={t(`customProjectModal.step2.compressorQtyPerCircuit`)}
        className="mb-3"
      >
        <Form.Control
          type="number"
          name="custom_system[compressor_quantity_per_circuit]"
          value={formData.custom_system.compressor_quantity_per_circuit}
          onChange={handleChange}
          required
          placeholder=""
          onWheel={disableNumberScroll}
          min={1}
          step="any"
        />
      </FloatingLabel>

      {/* Condenser Model */}
      <FloatingLabel
        controlId="floating_condenser_model"
        label={t(`customProjectModal.step2.condenserModel`)}
        className="mb-3"
      >
        <Form.Control
          type="text"
          name="custom_system[condenser_model]"
          value={formData.custom_system.condenser_model}
          onChange={handleChange}
          required
          placeholder=""
        />
      </FloatingLabel>

      {/* Condenser Quantity per Circuit */}
      <FloatingLabel
        controlId="floating_condenser_quantity_per_circuit"
        label={t(`customProjectModal.step2.condenserQtyPerCircuit`)}
        className="mb-3"
      >
        <Form.Control
          type="number"
          name="custom_system[condenser_quantity_per_circuit]"
          value={formData.custom_system.condenser_quantity_per_circuit}
          onChange={handleChange}
          required
          placeholder=""
          onWheel={disableNumberScroll}
          min={1}
          step="any"
        />
      </FloatingLabel>

      {/* Fan Model */}
      <FloatingLabel
        controlId="floating_fan_model"
        label={t(`customProjectModal.step2.fanModel`)}
        className="mb-3"
      >
        <Form.Control
          type="text"
          name="custom_system[fan_model]"
          value={formData.custom_system.fan_model}
          onChange={handleChange}
          required
          placeholder=""
        />
      </FloatingLabel>

      {/* Fan Quantity per Circuit */}
      <FloatingLabel
        controlId="floating_fan_quantity_per_circuit"
        label={t(`customProjectModal.step2.fanQtyPerCircuit`)}
        className="mb-3"
      >
        <Form.Control
          type="number"
          name="custom_system[fan_quantity_per_circuit]"
          value={formData.custom_system.fan_quantity_per_circuit}
          onChange={handleChange}
          required
          placeholder=""
          onWheel={disableNumberScroll}
          min={0}
          step="any"
        />
      </FloatingLabel>

      {/* Evaporator Model */}
      <FloatingLabel
        controlId="floating_evaporator_model"
        label={t(`customProjectModal.step2.evaporatorModel`)}
        className="mb-3"
      >
        <Form.Control
          type="text"
          name="custom_system[evaporator_model]"
          value={formData.custom_system.evaporator_model}
          onChange={handleChange}
          required
          placeholder=""
        />
      </FloatingLabel>

      {/* Evaporator Quantity */}
      <FloatingLabel
        controlId="floating_evaporator_quantity"
        label={t(`customProjectModal.step2.evaporatorQty`)}
        className="mb-3"
      >
        <Form.Control
          type="number"
          name="custom_system[evaporator_quantity]"
          value={formData.custom_system.evaporator_quantity}
          onChange={handleChange}
          required
          placeholder=""
          onWheel={disableNumberScroll}
          min={0}
          step="any"
        />
      </FloatingLabel>

      {/* Condensing Temperature */}
      <InputGroup className="mb-3">
        <FloatingLabel
          controlId="floating_condensing_temperature"
          label={t(`customProjectModal.step2.condensingTemperature`)}
        >
          <Form.Control
            type="number"
            name="custom_system[condensing_temperature]"
            value={formData.custom_system.condensing_temperature}
            onChange={handleChange}
            step="any"
            required
            placeholder=""
            onWheel={disableNumberScroll}
          />
        </FloatingLabel>
        <InputGroup.Text>{t(`units.${unitSystem}.temperature`)}</InputGroup.Text>
      </InputGroup>

      {/* Subcooling */}
      <InputGroup className="mb-3">
        <FloatingLabel
          controlId="floating_subcooling"
          label={t(`customProjectModal.step2.subcooling`)}
        >
          <Form.Control
            type="number"
            name="custom_system[subcooling]"
            value={formData.custom_system.subcooling}
            onChange={handleChange}
            required
            step="any"
            placeholder=""
            onWheel={disableNumberScroll}
          />
        </FloatingLabel>
        <InputGroup.Text>{t(`units.${unitSystem}.temperature`)}</InputGroup.Text>
      </InputGroup>

      {/* Evaporating Temperature */}
      <InputGroup className="mb-3">
        <FloatingLabel
          controlId="floating_evaporating_temperature"
          label={t(`customProjectModal.step2.evaporatingTemperature`)}
        >
          <Form.Control
            type="number"
            name="custom_system[evaporating_temperature]"
            value={formData.custom_system.evaporating_temperature}
            onChange={handleChange}
            required
            step="any"
            placeholder=""
            onWheel={disableNumberScroll}
          />
        </FloatingLabel>
        <InputGroup.Text>{t(`units.${unitSystem}.temperature`)}</InputGroup.Text>
      </InputGroup>

      {/* Superheat */}
      <InputGroup className="mb-3">
        <FloatingLabel
          controlId="floating_superheat"
          label={t(`customProjectModal.step2.superheat`)}
        >
          <Form.Control
            type="number"
            name="custom_system[superheat]"
            value={formData.custom_system.superheat}
            onChange={handleChange}
            required
            step="any"
            placeholder=""
            onWheel={disableNumberScroll}
          />
        </FloatingLabel>
        <InputGroup.Text>{t(`units.${unitSystem}.temperature`)}</InputGroup.Text>
      </InputGroup>

      <div className="d-flex justify-content-between mt-3 buttonsContainer">
        <GreenLabel onClick={onBack}>{t(`customProjectModal.step2.back`)}</GreenLabel>
        <ActionButton type="submit" width={"50%"}>
          {t(`customProjectModal.step2.create`)}
        </ActionButton>
      </div>
    </Form>
  );
};

export default CustomProjectStepTwo;
